import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import {
  deleteWindowFormDefinition,
  getWindowFormDefinitions,
  getWindowFormNotificationSettings,
  updateWindowFormDefinition,
  updateWindowFormNotificationSettings,
} from "../../../components/Forms/api";
import { WINDOW_FORM_DEFAULT_DEFINITIONS } from "../../../components/Forms/windowFormDefinitionDefaults";
import { mergeWindowFormDefinition } from "../../../components/Forms/windowFormDefinitionUtils";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState } from "../../../components/UI/States";
import {
  BackLink,
  Pill,
  Segmented,
} from "../../../components/WindowDoor/wdUi";

const createId = () => Math.random().toString(36).slice(2, 10);

const getAllFieldsForDefinition = (definition) => {
  const entries = [];
  (definition?.sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => {
      if (field?.id) {
        entries.push({
          id: field.id,
          label: field.label || "Untitled Field",
          sectionTitle: section.title || "Section",
          type: field.type || "text",
          options: Array.isArray(field.options) ? field.options : [],
        });
      }
    });
  });
  return entries;
};

const inputClass =
  "app-input rounded-xl border px-3 py-2 text-sm transition disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton = "app-btn app-btn-primary";
const secondaryButton = "app-btn app-btn-secondary";
const dangerButton =
  "app-btn app-btn-sm border-rose-500/30 text-rose-600 hover:bg-rose-500/10 dark:text-rose-400";

// Form manager for the window RFQ forms. Managers handle notification
// recipients; managers AND window quoters (Window Door Staff) can build and
// edit the form definitions.
const WindowFormsAdminSettings = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isManager =
    user?.roles?.includes("Window Door Manager") ||
    user?.roles?.includes("Admin");

  const [activeTab, setActiveTab] = useState("forms");
  const [notificationSettings, setNotificationSettings] = useState({
    to: [],
    cc: [],
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    to: "",
    cc: "",
  });
  const [formDefinitions, setFormDefinitions] = useState({});
  const [savedSnapshots, setSavedSnapshots] = useState({});
  const [formOptions, setFormOptions] = useState([]);
  const [activeFormId, setActiveFormId] = useState(null);
  const [definitionSaving, setDefinitionSaving] = useState(false);
  const [optionEditor, setOptionEditor] = useState({
    open: false,
    sectionId: null,
    fieldId: null,
  });
  const [optionPasteValue, setOptionPasteValue] = useState("");
  // Repeatable-section rows keep their condition editor collapsed; this
  // tracks which field ids are expanded.
  const [openConditionFields, setOpenConditionFields] = useState({});
  const [customFormDraft, setCustomFormDraft] = useState({ name: "" });
  const [customFormSaving, setCustomFormSaving] = useState(false);
  const [customFormRemoving, setCustomFormRemoving] = useState(null);
  const [customFormError, setCustomFormError] = useState("");

  const builtInFormIds = useMemo(
    () => new Set(Object.keys(WINDOW_FORM_DEFAULT_DEFINITIONS).map(Number)),
    []
  );

  const generateUniqueFormId = useCallback(() => {
    const existingIds = new Set(formOptions.map((option) => option.id));
    let candidate = Date.now();
    while (existingIds.has(candidate)) {
      candidate += 1;
    }
    return candidate;
  }, [formOptions]);

  const loadDefinitions = useCallback(() => {
    getWindowFormDefinitions((definitions) => {
      const customDefinitions = (definitions || [])
        .filter((definition) => !builtInFormIds.has(definition?.form_id))
        .sort((a, b) => (a.form_id || 0) - (b.form_id || 0));
      const mergedDefinitions = {};
      customDefinitions.forEach((definition) => {
        const formId = definition.form_id;
        const baseDefinition = {
          form_id: formId,
          form_name: definition?.form_name || "",
          sections: [],
          show_built_in: false,
        };
        mergedDefinitions[formId] = mergeWindowFormDefinition(
          baseDefinition,
          definition
        );
      });
      const customOptions = customDefinitions.map((definition) => ({
        id: definition.form_id,
        name: definition.form_name || `Custom Form ${definition.form_id}`,
        isCustom: true,
      }));
      setFormOptions(customOptions);
      setFormDefinitions(mergedDefinitions);
      setSavedSnapshots(JSON.parse(JSON.stringify(mergedDefinitions)));
      setActiveFormId((prev) => {
        if (customOptions.some((option) => option.id === prev)) {
          return prev;
        }
        return customOptions[0]?.id || null;
      });
    });
  }, [builtInFormIds]);

  useEffect(() => {
    getWindowFormNotificationSettings(setNotificationSettings);
    loadDefinitions();
  }, [loadDefinitions]);

  useEffect(() => {
    if (notificationSettings) {
      setNotificationForm({
        to: (notificationSettings.to || []).join(", "),
        cc: (notificationSettings.cc || []).join(", "),
      });
    }
  }, [notificationSettings]);

  const parseEmails = (value) => {
    if (!value) {
      return [];
    }
    return value
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
  };

  const handleNotificationSave = async () => {
    setNotificationSaving(true);
    await updateWindowFormNotificationSettings({
      to: parseEmails(notificationForm.to),
      cc: parseEmails(notificationForm.cc),
    });
    await getWindowFormNotificationSettings(setNotificationSettings);
    setNotificationSaving(false);
  };

  const activeDefinition =
    (activeFormId && formDefinitions[activeFormId]) ||
    (activeFormId
      ? {
          form_id: activeFormId,
          sections: [],
        }
      : null);

  const activeFormName =
    formOptions.find((option) => option.id === activeFormId)?.name ||
    activeDefinition?.form_name ||
    "";

  const hasUnsavedChanges =
    activeFormId &&
    JSON.stringify(formDefinitions[activeFormId] || null) !==
      JSON.stringify(savedSnapshots[activeFormId] || null);

  const availableFields = getAllFieldsForDefinition(activeDefinition);

  const updateActiveDefinition = (updater) => {
    if (!activeFormId) {
      return;
    }
    setFormDefinitions((prev) => {
      const current = prev[activeFormId] || {
        form_id: activeFormId,
        sections: [],
      };
      const updated = updater(current);
      return { ...prev, [activeFormId]: updated };
    });
  };

  const handleDefinitionSave = async () => {
    if (!activeFormId) {
      return;
    }
    setDefinitionSaving(true);
    await updateWindowFormDefinition(activeFormId, activeDefinition);
    setSavedSnapshots((prev) => ({
      ...prev,
      [activeFormId]: JSON.parse(JSON.stringify(activeDefinition)),
    }));
    setDefinitionSaving(false);
  };

  const handleCustomFormAdd = async () => {
    setCustomFormError("");
    if (!customFormDraft.name.trim()) {
      setCustomFormError("Enter a name for the new form.");
      return;
    }
    const newFormId = generateUniqueFormId();
    setCustomFormSaving(true);
    await updateWindowFormDefinition(newFormId, {
      form_id: newFormId,
      form_name: customFormDraft.name.trim(),
      sections: [],
      show_built_in: false,
    });
    setCustomFormSaving(false);
    setCustomFormDraft({ name: "" });
    loadDefinitions();
    setActiveFormId(newFormId);
  };

  const handleCustomFormRemove = async (formId) => {
    const formName =
      formOptions.find((option) => option.id === formId)?.name || "this form";
    const confirmed = window.confirm(
      `Delete "${formName}"? Salespeople will no longer be able to submit it. This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }
    setCustomFormRemoving(formId);
    await deleteWindowFormDefinition(formId);
    setCustomFormRemoving(null);
    loadDefinitions();
    if (activeFormId === formId) {
      setActiveFormId(null);
    }
  };

  const handleSectionTitleChange = (sectionId, value) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId ? { ...section, title: value } : section
      ),
    }));
  };

  const handleSectionTypeChange = (sectionId, value) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId ? { ...section, type: value } : section
      ),
    }));
  };

  const getMaxColumnsForSection = (sectionType) =>
    sectionType === "repeatable" ? 10 : 6;

  const handleSectionColumnsChange = (sectionId, sectionType, value) => {
    const maxColumns = getMaxColumnsForSection(sectionType);
    const parsed = Math.min(Math.max(parseInt(value, 10) || 2, 1), maxColumns);
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId ? { ...section, columns: parsed } : section
      ),
    }));
  };

  const handleAddSection = () => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: [
        ...(current.sections || []),
        {
          id: createId(),
          title: "New Section",
          fields: [],
          kind: "custom",
          type: "standard",
          columns: 2,
        },
      ],
    }));
  };

  const handleRemoveSection = (sectionId) => {
    const section = (activeDefinition?.sections || []).find(
      (item) => item.id === sectionId
    );
    if (
      section?.fields?.length &&
      !window.confirm(
        `Remove the "${section.title || "Untitled"}" section and its ${
          section.fields.length
        } field${section.fields.length === 1 ? "" : "s"}?`
      )
    ) {
      return;
    }
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).filter(
        (item) => item.id !== sectionId
      ),
    }));
  };

  const handleAddField = (sectionId) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: [
                ...(section.fields || []),
                {
                  id: createId(),
                  label: "New Field",
                  type: "text",
                  options: [],
                  conditions: [],
                  condition_match: "all",
                  kind: section.kind === "builtin" ? "builtin" : "custom",
                },
              ],
            }
          : section
      ),
    }));
  };

  const handleRemoveField = (sectionId, fieldId) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).filter(
                (field) => field.id !== fieldId
              ),
            }
          : section
      ),
    }));
  };

  const handleFieldChange = (sectionId, fieldId, key, value) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) => {
                if (field.id !== fieldId) {
                  return field;
                }
                const updatedField = { ...field, [key]: value };
                if (
                  key === "type" &&
                  (value === "dropdown" || value === "radio")
                ) {
                  updatedField.options = updatedField.options?.length
                    ? updatedField.options
                    : [""];
                }
                if (
                  key === "type" &&
                  value !== "dropdown" &&
                  value !== "radio"
                ) {
                  updatedField.options = [];
                }
                return updatedField;
              }),
            }
          : section
      ),
    }));
  };

  const handleAddCondition = (sectionId, fieldId) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) => {
                if (field.id !== fieldId) {
                  return field;
                }
                return {
                  ...field,
                  condition_match: field.condition_match || "all",
                  conditions: [
                    ...(Array.isArray(field.conditions)
                      ? field.conditions
                      : []),
                    {
                      id: createId(),
                      field_id: "",
                      value: "",
                    },
                  ],
                };
              }),
            }
          : section
      ),
    }));
  };

  const handleConditionChange = (
    sectionId,
    fieldId,
    conditionId,
    key,
    value
  ) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) => {
                if (field.id !== fieldId) {
                  return field;
                }
                return {
                  ...field,
                  conditions: (field.conditions || []).map((condition) => {
                    if (condition.id !== conditionId) {
                      return condition;
                    }
                    const nextCondition = { ...condition, [key]: value };
                    if (key === "field_id") {
                      nextCondition.value = "";
                    }
                    return nextCondition;
                  }),
                };
              }),
            }
          : section
      ),
    }));
  };

  const handleRemoveCondition = (sectionId, fieldId, conditionId) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) => {
                if (field.id !== fieldId) {
                  return field;
                }
                return {
                  ...field,
                  conditions: (field.conditions || []).filter(
                    (condition) => condition.id !== conditionId
                  ),
                };
              }),
            }
          : section
      ),
    }));
  };

  const reorderItems = (items, startIndex, endIndex) => {
    const result = Array.from(items);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const moveSection = (sectionId, direction) => {
    updateActiveDefinition((current) => {
      const sections = current.sections || [];
      const index = sections.findIndex((section) => section.id === sectionId);
      if (index === -1) {
        return current;
      }
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= sections.length) {
        return current;
      }
      return {
        ...current,
        sections: reorderItems(sections, index, targetIndex),
      };
    });
  };

  const moveField = (sectionId, fieldId, direction) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) => {
        if (section.id !== sectionId) {
          return section;
        }
        const fields = section.fields || [];
        const index = fields.findIndex((field) => field.id === fieldId);
        if (index === -1) {
          return section;
        }
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= fields.length) {
          return section;
        }
        return {
          ...section,
          fields: reorderItems(fields, index, targetIndex),
        };
      }),
    }));
  };

  const handleAddOption = (sectionId, fieldId) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: [...(field.options || []), ""],
                    }
                  : field
              ),
            }
          : section
      ),
    }));
  };

  const handleOptionPaste = (sectionId, fieldId) => {
    const nextOptions = optionPasteValue
      .split(/\r?\n/)
      .map((option) => option.trim())
      .filter((option) => option);
    if (!nextOptions.length) {
      return;
    }
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: [...(field.options || []), ...nextOptions],
                    }
                  : field
              ),
            }
          : section
      ),
    }));
    setOptionPasteValue("");
  };

  const handleOptionChange = (sectionId, fieldId, optionIndex, value) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: (field.options || []).map((option, index) =>
                        index === optionIndex ? value : option
                      ),
                    }
                  : field
              ),
            }
          : section
      ),
    }));
  };

  const handleRemoveOption = (sectionId, fieldId, optionIndex) => {
    updateActiveDefinition((current) => ({
      ...current,
      sections: (current.sections || []).map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: (section.fields || []).map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      options: (field.options || []).filter(
                        (_, index) => index !== optionIndex
                      ),
                    }
                  : field
              ),
            }
          : section
      ),
    }));
  };

  const activeOptionField = (activeDefinition?.sections || [])
    .flatMap((section) =>
      (section.fields || []).map((field) => ({
        ...field,
        sectionId: section.id,
      }))
    )
    .find(
      (field) =>
        field.sectionId === optionEditor.sectionId &&
        field.id === optionEditor.fieldId
    );

  const renderConditionsEditor = (section, field) => {
    const conditionFieldOptions = availableFields.filter(
      (option) => option.id !== field.id
    );
    const conditions = Array.isArray(field.conditions) ? field.conditions : [];
    return (
      <div className="app-surface space-y-2 rounded-xl border p-3">
        <div className="flex items-center justify-between gap-2">
          <label className="app-muted text-xs font-semibold">
            Conditional Visibility
          </label>
          {!!conditions.length && (
            <select
              aria-label="Condition match"
              className={`${inputClass} !rounded-lg !px-2 !py-1 text-xs`}
              value={field.condition_match || "all"}
              onChange={(event) =>
                handleFieldChange(
                  section.id,
                  field.id,
                  "condition_match",
                  event.target.value
                )
              }
            >
              <option value="all">Match ALL</option>
              <option value="any">Match ANY</option>
            </select>
          )}
        </div>
        {conditions.map((condition) => {
          const selectedConditionField = conditionFieldOptions.find(
            (option) => option.id === condition.field_id
          );
          const selectedFieldIsDropdown =
            selectedConditionField?.type === "dropdown";
          return (
            <div
              key={condition.id}
              className="grid grid-cols-1 gap-2 md:grid-cols-12"
            >
              <select
                className={`md:col-span-6 ${inputClass} !rounded-lg !px-2.5 text-xs`}
                value={condition.field_id || ""}
                onChange={(event) =>
                  handleConditionChange(
                    section.id,
                    field.id,
                    condition.id,
                    "field_id",
                    event.target.value
                  )
                }
              >
                <option value="">Select field...</option>
                {conditionFieldOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.sectionTitle} - {option.label}
                  </option>
                ))}
              </select>
              {selectedFieldIsDropdown ? (
                <select
                  className={`md:col-span-4 ${inputClass} !rounded-lg !px-2.5 text-xs`}
                  value={condition.value || ""}
                  onChange={(event) =>
                    handleConditionChange(
                      section.id,
                      field.id,
                      condition.id,
                      "value",
                      event.target.value
                    )
                  }
                >
                  <option value="">Select value...</option>
                  {(selectedConditionField?.options || []).map(
                    (optionValue, optionIndex) => (
                      <option
                        key={`${selectedConditionField.id}-${optionIndex}`}
                        value={optionValue}
                      >
                        {optionValue}
                      </option>
                    )
                  )}
                </select>
              ) : (
                <input
                  className={`md:col-span-4 ${inputClass} !rounded-lg !px-2.5 text-xs`}
                  value={condition.value || ""}
                  onChange={(event) =>
                    handleConditionChange(
                      section.id,
                      field.id,
                      condition.id,
                      "value",
                      event.target.value
                    )
                  }
                  placeholder="Equals value"
                />
              )}
              <button
                type="button"
                className={`md:col-span-2 ${dangerButton} justify-center`}
                onClick={() =>
                  handleRemoveCondition(section.id, field.id, condition.id)
                }
              >
                Remove
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className={`${secondaryButton} app-btn-sm`}
          onClick={() => handleAddCondition(section.id, field.id)}
        >
          Add Condition
        </button>
      </div>
    );
  };

  const backPath = isManager
    ? "/windowdoor/quote-manager"
    : "/windowdoor/staff/windowforms";
  const backLabel = isManager ? "Quote Manager" : "My Assigned RFQs";

  const tabs = [
    { key: "forms", label: "Forms" },
    ...(isManager ? [{ key: "notifications", label: "Notifications" }] : []),
  ];

  const renderNotificationsTab = () => (
    <div className="app-card rounded-2xl border p-5">
      <h3 className="text-lg font-semibold">Notification Recipients</h3>
      <p className="app-muted mt-0.5 text-sm">
        These emails receive window form notifications. The submitting
        salesperson is automatically CC&apos;d.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
        <div>
          <label
            htmlFor="wf-notify-to"
            className="mb-1.5 block text-sm font-semibold"
          >
            To
          </label>
          <input
            id="wf-notify-to"
            className={`w-full ${inputClass}`}
            value={notificationForm.to}
            onChange={(event) =>
              setNotificationForm((prev) => ({
                ...prev,
                to: event.target.value,
              }))
            }
            placeholder="blake.upham@bigclumber.com"
          />
        </div>
        <div>
          <label
            htmlFor="wf-notify-cc"
            className="mb-1.5 block text-sm font-semibold"
          >
            CC
          </label>
          <input
            id="wf-notify-cc"
            className={`w-full ${inputClass}`}
            value={notificationForm.cc}
            onChange={(event) =>
              setNotificationForm((prev) => ({
                ...prev,
                cc: event.target.value,
              }))
            }
            placeholder="kurt.gibson@bigclumber.com"
          />
        </div>
      </div>
      <p className="app-muted mt-2 text-xs">
        Separate multiple addresses with commas.
      </p>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className={primaryButton}
          onClick={handleNotificationSave}
          disabled={notificationSaving}
        >
          {notificationSaving ? "Saving..." : "Save Notifications"}
        </button>
      </div>
    </div>
  );

  const renderFormList = () => (
    <div className="app-card rounded-2xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Your Forms</h3>
          <p className="app-muted mt-0.5 text-sm">
            These are the RFQ forms salespeople can submit. Pick one to edit
            its sections and fields below.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {formOptions.length ? (
          formOptions.map((option) => {
            const definition = formDefinitions[option.id];
            const sectionCount = definition?.sections?.length || 0;
            const fieldCount = getAllFieldsForDefinition(definition).length;
            const isActive = option.id === activeFormId;
            return (
              <div
                key={`custom-form-${option.id}`}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm transition ${
                  isActive
                    ? "border-sky-500/40 bg-sky-500/10"
                    : "app-surface"
                }`}
              >
                <div>
                  <p className="font-semibold">{option.name}</p>
                  <p className="app-muted text-xs">
                    {sectionCount} section{sectionCount === 1 ? "" : "s"} •{" "}
                    {fieldCount} field{fieldCount === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={
                      isActive
                        ? `${primaryButton} app-btn-sm`
                        : `${secondaryButton} app-btn-sm`
                    }
                    onClick={() => setActiveFormId(option.id)}
                  >
                    {isActive ? "Editing" : "Edit"}
                  </button>
                  <button
                    type="button"
                    className={dangerButton}
                    onClick={() => handleCustomFormRemove(option.id)}
                    disabled={customFormRemoving === option.id}
                  >
                    {customFormRemoving === option.id
                      ? "Removing..."
                      : "Delete"}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <p className="app-muted text-sm">
            No forms yet — create the first one below.
          </p>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t app-divider pt-4">
        <input
          aria-label="New form name"
          className={`min-w-[220px] flex-1 ${inputClass}`}
          value={customFormDraft.name}
          onChange={(event) =>
            setCustomFormDraft((prev) => ({
              ...prev,
              name: event.target.value,
            }))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleCustomFormAdd();
            }
          }}
          placeholder="New form name (e.g. Pella Windows RFQ)"
        />
        <button
          type="button"
          className={primaryButton}
          onClick={handleCustomFormAdd}
          disabled={customFormSaving}
        >
          {customFormSaving ? "Creating..." : "Create Form"}
        </button>
      </div>
      {customFormError && (
        <p className="mt-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {customFormError}
        </p>
      )}
    </div>
  );

  const renderBuilder = () => {
    if (!activeFormId) {
      return (
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={DashboardCustomizeOutlinedIcon}
            title="No form selected"
            description="Create a form above or pick one from the list to start building."
          />
        </div>
      );
    }
    return (
      <div className="app-card rounded-2xl border p-5">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              Editing: {activeFormName}
            </h3>
            <p className="app-muted mt-0.5 text-sm">
              Add sections and fields, then save. Changes go live for
              salespeople as soon as you save.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasUnsavedChanges && (
              <Pill tone="amber">Unsaved changes</Pill>
            )}
            <button
              type="button"
              className={secondaryButton}
              onClick={handleAddSection}
            >
              Add Section
            </button>
            <button
              type="button"
              className={primaryButton}
              onClick={handleDefinitionSave}
              disabled={definitionSaving || !hasUnsavedChanges}
            >
              {definitionSaving ? "Saving..." : "Save Form"}
            </button>
          </div>
        </div>

        {activeDefinition?.sections?.length ? (
          <div className="space-y-5">
            {activeDefinition.sections.map((section, index) => {
              const isRepeatable = section.type === "repeatable";
              const sectionAccent = isRepeatable
                ? "bg-indigo-400"
                : "bg-slate-400";

              return (
                <div
                  key={section.id}
                  className="overflow-hidden rounded-2xl border app-divider"
                >
                  <div className="app-surface flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className={`${secondaryButton} app-btn-sm !px-2.5`}
                          onClick={() => moveSection(section.id, -1)}
                          disabled={index === 0}
                          aria-label="Move section up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className={`${secondaryButton} app-btn-sm !px-2.5`}
                          onClick={() => moveSection(section.id, 1)}
                          disabled={
                            index === activeDefinition.sections.length - 1
                          }
                          aria-label="Move section down"
                        >
                          ↓
                        </button>
                      </div>

                      <input
                        aria-label="Section title"
                        className={`w-full md:w-1/3 ${inputClass}`}
                        value={section.title || ""}
                        onChange={(event) =>
                          handleSectionTitleChange(
                            section.id,
                            event.target.value
                          )
                        }
                        placeholder="Section title"
                      />

                      <select
                        aria-label="Section type"
                        className={inputClass}
                        value={section.type || "standard"}
                        onChange={(event) =>
                          handleSectionTypeChange(
                            section.id,
                            event.target.value
                          )
                        }
                      >
                        <option value="standard">Standard Section</option>
                        <option value="repeatable">Repeatable Rows</option>
                      </select>

                      <select
                        aria-label="Section columns"
                        className={inputClass}
                        value={section.columns || 2}
                        onChange={(event) =>
                          handleSectionColumnsChange(
                            section.id,
                            section.type,
                            event.target.value
                          )
                        }
                      >
                        {Array.from(
                          {
                            length: getMaxColumnsForSection(section.type),
                          },
                          (_, idx) => {
                            const value = idx + 1;
                            return (
                              <option key={value} value={value}>
                                {value} Column{value === 1 ? "" : "s"}
                              </option>
                            );
                          }
                        )}
                      </select>

                      {section.kind === "builtin" && (
                        <Pill tone="amber">Built-in</Pill>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className={`${secondaryButton} app-btn-sm`}
                        onClick={() => handleAddField(section.id)}
                      >
                        Add Field
                      </button>
                      <button
                        type="button"
                        className={dangerButton}
                        onClick={() => handleRemoveSection(section.id)}
                      >
                        Remove Section
                      </button>
                    </div>
                  </div>

                  {!(section.fields || []).length ? (
                    <p className="app-muted p-4 text-sm">
                      No fields yet. Add a field to start building this
                      section.
                    </p>
                  ) : isRepeatable ? (
                    <div className="space-y-2 p-4">
                      <p className="app-muted text-xs">
                        Each field is one column of the row. Top to bottom
                        here is left to right on the salesperson form.
                      </p>
                      {section.fields.map((field, fieldIndex) => {
                        const conditionCount = Array.isArray(field.conditions)
                          ? field.conditions.length
                          : 0;
                        const conditionsOpen = Boolean(
                          openConditionFields[field.id]
                        );
                        return (
                          <div
                            key={field.id}
                            className="app-surface rounded-xl border"
                          >
                            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                              <span className="app-muted w-12 shrink-0 text-xs font-semibold tabular-nums">
                                Col {fieldIndex + 1}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  className={`${secondaryButton} app-btn-sm !px-2.5`}
                                  onClick={() =>
                                    moveField(section.id, field.id, -1)
                                  }
                                  disabled={fieldIndex === 0}
                                  aria-label="Move column up"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className={`${secondaryButton} app-btn-sm !px-2.5`}
                                  onClick={() =>
                                    moveField(section.id, field.id, 1)
                                  }
                                  disabled={
                                    fieldIndex === section.fields.length - 1
                                  }
                                  aria-label="Move column down"
                                >
                                  ↓
                                </button>
                              </div>
                              <input
                                aria-label="Field label"
                                className={`min-w-[160px] flex-1 ${inputClass}`}
                                value={field.label || ""}
                                onChange={(event) =>
                                  handleFieldChange(
                                    section.id,
                                    field.id,
                                    "label",
                                    event.target.value
                                  )
                                }
                                placeholder="Field label"
                              />
                              <select
                                aria-label="Field type"
                                className={inputClass}
                                value={field.type || "text"}
                                onChange={(event) =>
                                  handleFieldChange(
                                    section.id,
                                    field.id,
                                    "type",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="text">Text</option>
                                <option value="dropdown">Dropdown</option>
                                <option value="radio">Radio Buttons</option>
                                <option value="date">Date Picker</option>
                                <option value="quantity">Qty Selector</option>
                                <option value="spacer">Spacer</option>
                              </select>
                              <label className="flex min-h-[32px] cursor-pointer items-center gap-1.5 text-xs font-semibold">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-sky-600"
                                  checked={Boolean(field.required)}
                                  onChange={(event) =>
                                    handleFieldChange(
                                      section.id,
                                      field.id,
                                      "required",
                                      event.target.checked
                                    )
                                  }
                                />
                                Required
                              </label>
                              {(field.type === "dropdown" ||
                                field.type === "radio") && (
                                <button
                                  type="button"
                                  className={`${secondaryButton} app-btn-sm`}
                                  onClick={() => {
                                    setOptionPasteValue("");
                                    setOptionEditor({
                                      open: true,
                                      sectionId: section.id,
                                      fieldId: field.id,
                                    });
                                  }}
                                >
                                  Values ({(field.options || []).length})
                                </button>
                              )}
                              <button
                                type="button"
                                aria-expanded={conditionsOpen}
                                className={`${secondaryButton} app-btn-sm ${
                                  conditionsOpen ? "ring-2 ring-sky-500/40" : ""
                                }`}
                                onClick={() =>
                                  setOpenConditionFields((prev) => ({
                                    ...prev,
                                    [field.id]: !prev[field.id],
                                  }))
                                }
                              >
                                Conditions ({conditionCount})
                              </button>
                              <button
                                type="button"
                                className={dangerButton}
                                onClick={() =>
                                  handleRemoveField(section.id, field.id)
                                }
                              >
                                Remove
                              </button>
                            </div>
                            {conditionsOpen && (
                              <div className="border-t app-divider p-3">
                                {renderConditionsEditor(section, field)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4">
                      <p className="app-muted mb-3 text-xs">
                        Shown to salespeople in {section.columns || 2} column
                        {(section.columns || 2) === 1 ? "" : "s"} — the layout
                        here is just for editing.
                      </p>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {section.fields.map((field, fieldIndex) => {
                        return (
                          <div
                            key={field.id}
                            className="app-surface relative overflow-hidden rounded-xl border"
                          >
                            <div
                              className={`absolute left-0 top-0 h-full w-1 ${sectionAccent}`}
                            />
                            <div className="space-y-2 p-3 pl-4">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    className={`${secondaryButton} app-btn-sm !px-2.5`}
                                    onClick={() =>
                                      moveField(section.id, field.id, -1)
                                    }
                                    disabled={fieldIndex === 0}
                                    aria-label="Move field left"
                                  >
                                    ←
                                  </button>
                                  <button
                                    type="button"
                                    className={`${secondaryButton} app-btn-sm !px-2.5`}
                                    onClick={() =>
                                      moveField(section.id, field.id, 1)
                                    }
                                    disabled={
                                      fieldIndex === section.fields.length - 1
                                    }
                                    aria-label="Move field right"
                                  >
                                    →
                                  </button>
                                </div>
                                <label className="app-muted block text-xs font-semibold">
                                  Field Label
                                </label>
                              </div>

                              <input
                                aria-label="Field label"
                                className={`w-full ${inputClass}`}
                                value={field.label || ""}
                                onChange={(event) =>
                                  handleFieldChange(
                                    section.id,
                                    field.id,
                                    "label",
                                    event.target.value
                                  )
                                }
                                placeholder="Field label"
                              />

                              <div className="flex flex-wrap items-center gap-2">
                                <div className="min-w-[160px] flex-1">
                                  <label className="app-muted mb-1 block text-xs font-semibold">
                                    Field Type
                                  </label>
                                  <select
                                    aria-label="Field type"
                                    className={`w-full ${inputClass}`}
                                    value={field.type || "text"}
                                    onChange={(event) =>
                                      handleFieldChange(
                                        section.id,
                                        field.id,
                                        "type",
                                        event.target.value
                                      )
                                    }
                                  >
                                    <option value="text">Text</option>
                                    <option value="dropdown">Dropdown</option>
                                    <option value="radio">
                                      Radio Buttons
                                    </option>
                                    <option value="date">Date Picker</option>
                                    <option value="quantity">
                                      Qty Selector
                                    </option>
                                    <option value="spacer">Spacer</option>
                                  </select>
                                </div>

                                <label className="flex min-h-[32px] cursor-pointer items-center gap-2 text-xs font-semibold">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-sky-600"
                                    checked={Boolean(field.required)}
                                    onChange={(event) =>
                                      handleFieldChange(
                                        section.id,
                                        field.id,
                                        "required",
                                        event.target.checked
                                      )
                                    }
                                  />
                                  Required
                                </label>

                                {(field.type === "dropdown" ||
                                  field.type === "radio") && (
                                  <div className="min-w-[160px] flex-1">
                                    <label className="app-muted mb-1 block text-xs font-semibold">
                                      Option Values
                                    </label>
                                    <button
                                      type="button"
                                      className={`w-full ${secondaryButton} app-btn-sm`}
                                      onClick={() => {
                                        setOptionPasteValue("");
                                        setOptionEditor({
                                          open: true,
                                          sectionId: section.id,
                                          fieldId: field.id,
                                        });
                                      }}
                                    >
                                      Edit Values (
                                      {(field.options || []).length})
                                    </button>
                                  </div>
                                )}
                              </div>

                              {renderConditionsEditor(section, field)}

                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  className={dangerButton}
                                  onClick={() =>
                                    handleRemoveField(section.id, field.id)
                                  }
                                >
                                  Remove Field
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="app-muted text-sm">
            No sections yet. Add a section to start building this form.
          </p>
        )}
      </div>
    );
  };

  return (
    <PageShell size="2xl">
      <BackLink onClick={() => navigate(backPath)}>
        Back to {backLabel}
      </BackLink>
      <PageHeader
        icon={DashboardCustomizeOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Window Form Manager"
        description={`Build the RFQ forms salespeople fill out${
          isManager ? " and manage who gets notified" : ""
        }.`}
      >
        {tabs.length > 1 && (
          <div className="mt-5">
            <Segmented
              ariaLabel="Form Manager sections"
              value={activeTab}
              onChange={setActiveTab}
              options={tabs}
            />
          </div>
        )}
      </PageHeader>

      <div className="space-y-6">
        {activeTab === "notifications" && isManager
          ? renderNotificationsTab()
          : (
            <>
              {renderFormList()}
              {renderBuilder()}
            </>
          )}
      </div>

      {optionEditor.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-scrim)] px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Option Values"
            className="app-glass app-glass-rim max-h-[85vh] w-full max-w-lg overflow-hidden rounded-3xl border p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="app-eyebrow">Option Values</p>
                <h3 className="text-lg font-semibold">
                  {activeOptionField?.label || "Edit Values"}
                </h3>
              </div>
              <button
                type="button"
                className={primaryButton}
                onClick={() => {
                  setOptionPasteValue("");
                  setOptionEditor({
                    open: false,
                    sectionId: null,
                    fieldId: null,
                  });
                }}
              >
                Done
              </button>
            </div>

            <div className="mt-4 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              {(activeOptionField?.options || []).map((option, index) => (
                <div
                  key={`${activeOptionField?.id || "field"}-option-${index}`}
                  className="flex items-center gap-2"
                >
                  <input
                    aria-label={`Value ${index + 1}`}
                    className={`min-w-0 flex-1 ${inputClass}`}
                    value={option}
                    onChange={(event) =>
                      handleOptionChange(
                        optionEditor.sectionId,
                        optionEditor.fieldId,
                        index,
                        event.target.value
                      )
                    }
                    placeholder="Dropdown value"
                  />
                  <button
                    type="button"
                    className={dangerButton}
                    onClick={() =>
                      handleRemoveOption(
                        optionEditor.sectionId,
                        optionEditor.fieldId,
                        index
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                type="button"
                className={`${secondaryButton} app-btn-sm`}
                onClick={() =>
                  handleAddOption(optionEditor.sectionId, optionEditor.fieldId)
                }
              >
                Add Dropdown Value
              </button>

              <div className="app-surface rounded-xl border p-3">
                <label
                  htmlFor="wf-option-paste"
                  className="app-muted mb-2 block text-xs font-semibold"
                >
                  Paste Values (one per line)
                </label>
                <textarea
                  id="wf-option-paste"
                  className={`min-h-[120px] w-full ${inputClass}`}
                  value={optionPasteValue}
                  onChange={(event) => setOptionPasteValue(event.target.value)}
                  placeholder={"1\n2\n3\n4"}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    className={`${secondaryButton} app-btn-sm`}
                    onClick={() =>
                      handleOptionPaste(
                        optionEditor.sectionId,
                        optionEditor.fieldId
                      )
                    }
                    disabled={!optionPasteValue.trim()}
                  >
                    Add List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default WindowFormsAdminSettings;
